import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { assertNonNullable } from '@examdojo/util/assert';
import { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import { Editor } from '@tiptap/core';
import { TextSelection } from 'prosemirror-state';

@Component({
  selector: 'dojo-tiptap-table-of-contents',
  imports: [NgClass, MarkdownViewerComponent],
  templateUrl: './tiptap-table-of-contents.component.html',
  styleUrl: './tiptap-table-of-contents.component.scss',
  host: { class: 'text-sm flex flex-col text-decoration-none' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiptapTableOfContentsComponent {
  readonly tableOfContentData = input.required<TableOfContentData>();
  readonly editor = input.required<Editor>();
  readonly scrollableParent = input<HTMLElement>();

  /**
   * When no item is marked as active,
   * mark the first item as active for better UX.
   */
  readonly processedData = computed<TableOfContentData>(() => {
    const data = this.tableOfContentData();
    if (!data.length) {
      return data;
    }

    const hasActiveItem = data.some((item) => item.isActive);
    if (hasActiveItem) {
      return data;
    }

    return data.map((item, index) => (index === 0 ? { ...item, isActive: true } : item));
  });

  onItemClick(id: string) {
    const editor = this.editor();
    const scrollableParent = this.scrollableParent();

    if (!scrollableParent) {
      return;
    }

    const element = editor.view.dom.querySelector(`[data-toc-id="${id}"`);
    assertNonNullable(element, 'element');

    const pos = editor.view.posAtDOM(element, 0);

    // set focus
    const tr = editor.view.state.tr;
    tr.setSelection(new TextSelection(tr.doc.resolve(pos)));
    editor.view.dispatch(tr);
    editor.view.focus();

    const editorElement = editor.view.dom;

    const targetRect = element.getBoundingClientRect();
    const scrollableParentRect = scrollableParent.getBoundingClientRect();
    const targetPositionRelativeToParent = targetRect.top - scrollableParentRect.top + scrollableParent.scrollTop;
    const editorPositionRelativeToParent =
      editorElement.getBoundingClientRect().top - scrollableParentRect.top + scrollableParent.scrollTop;

    // When computing the active heading TipTip adds an offset equal to the editor's top position in relation to the scrollable parent.
    // We apply the same offset to synchronize the navigation
    const targetPosition = targetPositionRelativeToParent - editorPositionRelativeToParent;

    scrollableParent.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }
}
